import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api';
import DataTable from '../components/DataTable';

const DURUMLAR = {
  BASVURU: 'badge badge-basvuru',
  ON_KONTROL: 'badge badge-on_kontrol',
  SON_KONTROL: 'badge badge-son_kontrol',
  ONAYLANDI: 'badge badge-onaylandi',
  IPTAL: 'badge badge-iptal',
  ASKIDA: 'badge badge-askida',
  INTIKAL_RUHSAT: 'badge badge-on_kontrol',
};
const DURUM_ETIKET = {
  BASVURU: 'Beklemede', ONAYLANDI: 'Tamamlandı', IPTAL: 'İptal Ruhsat',
  INTIKAL_RUHSAT: 'İntikal Ruhsat', SON_KONTROL: 'Son Kontrol Bekliyor',
};

export default function RuhsatListesi() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [ruhsatlar, setRuhsatlar] = useState([]);
  const [tumVeri, setTumVeri] = useState([]);
  const [toplam, setToplam] = useState(0);
  const [sayfa, setSayfa] = useState(1);
  const [yukleniyor, setYukleniyor] = useState(false);
  const bugunTarih = new Date().toISOString().split('T')[0];
  const [tumRuhsatlar, setTumRuhsatlar] = useState(false);
  const [filtre, setFiltre] = useState({
    q: '', durum: params.get('durum') || '', sinif_adi: '', yil: '',
    basvuru_tarihi: params.get('durum') ? '' : bugunTarih,
  });
  const limit = 50;

  // SMS state
  const [smsModal, setSmsModal] = useState(false);
  const [smsSecimler, setSmsSecimler] = useState([]); // seçili ruhsat id listesi
  const [smsHedefTum, setSmsHedefTum] = useState(true); // true=filtreli tüm liste, false=sadece seçilenler
  const [smsSablonlar, setSmsSablonlar] = useState([]);
  const [smsSablonId, setSmsSablonId] = useState('');
  const [smsMesaj, setSmsMesaj] = useState('');
  const [smsGonderiyor, setSmsGonderiyor] = useState(false);
  const [smsSonuc, setSmsSonuc] = useState(null);

  const yukle = useCallback(async () => {
    setYukleniyor(true);
    try {
      const p = new URLSearchParams({ ...filtre, sayfa, limit });
      Object.keys(filtre).forEach(k => !filtre[k] && p.delete(k));
      const r = await api.get(`/ruhsat?${p}`);
      setRuhsatlar(r.data.veriler);
      setToplam(r.data.toplam);
    } catch (err) {
      console.error(err);
    } finally { setYukleniyor(false); }
  }, [filtre, sayfa]);

  // Export için tüm veriyi çek
  const tumVeriYukle = useCallback(async () => {
    try {
      const p = new URLSearchParams({ ...filtre, limit: 99999 });
      Object.keys(filtre).forEach(k => !filtre[k] && p.delete(k));
      const r = await api.get(`/ruhsat?${p}`);
      setTumVeri(r.data.veriler || []);
    } catch {}
  }, [filtre]);

  useEffect(() => { yukle(); }, [yukle]);
  useEffect(() => { tumVeriYukle(); }, [tumVeriYukle]);

  const smsPanelAc = () => {
    api.get('/sms/sablonlar').then(r => setSmsSablonlar(r.data.filter(s => s.aktif))).catch(() => {});
    // smsSecimler sıfırlanmıyor — tablodaki seçimler korunur
    setSmsHedefTum(smsSecimler.length === 0); // seçili varsa otomatik "seçilenler" moduna geç
    setSmsSablonId('');
    setSmsMesaj('');
    setSmsSonuc(null);
    setSmsModal(true);
  };

  const smsGonder = async () => {
    const hedef = smsHedefTum ? tumVeri.map(r => r.id) : smsSecimler;
    if (hedef.length === 0) return;
    const mesajMetni = smsMesaj || (smsSablonlar.find(s => String(s.id) === String(smsSablonId))?.icerik || '');
    if (!mesajMetni) return;
    setSmsGonderiyor(true); setSmsSonuc(null);
    try {
      const r = await api.post('/sms/gonder', { ruhsat_ids: hedef, mesaj: mesajMetni, sablon_id: smsSablonId || undefined });
      setSmsSonuc(r.data);
    } catch (e) { setSmsSonuc({ hata: e.response?.data?.hata || 'Gönderim hatası' }); }
    finally { setSmsGonderiyor(false); }
  };

  const toplamSayfa = Math.ceil(toplam / limit);

  const fmt = (d) => d ? new Date(d).toLocaleDateString('tr-TR') : '';

  const columns = [
    {
      key: 'rn', label: 'Ruhsat No', width: '110px',
      render: v => <strong style={{ color: '#1a237e' }}>{v}</strong>,
      exportValue: r => r.rn,
    },
    {
      key: 'unvan', label: 'Ünvan / İşyeri',
      render: v => <div style={{ fontWeight: '600' }}>{v}</div>,
      exportValue: r => r.unvan,
    },
    { key: 'adi', label: 'Adı', hidden: true, render: v => v || '—', exportValue: r => r.adi },
    { key: 'soyadi', label: 'Soyadı', hidden: true, render: v => v || '—', exportValue: r => r.soyadi },
    { key: 'ruhsat_turu_adi', label: 'Ruhsat Türü', hidden: true, render: v => v || '—', exportValue: r => r.ruhsat_turu_adi },
    {
      key: 'tcno', label: 'TC Kimlik No', width: '120px',
      render: (v, r) => <span style={{ fontSize: '12px' }}>{v || r.vn || '—'}</span>,
      exportValue: r => r.tcno || r.vn,
    },
    { key: 'vn', label: 'Vergi No', width: '120px', hidden: true, render: v => v || '—', exportValue: r => r.vn },
    { key: 'vd', label: 'Vergi Dairesi', hidden: true, render: v => v || '—', exportValue: r => r.vd },
    {
      key: 'isyeri_adresi', label: 'İşyeri Adresi',
      width: '260px',
      render: v => <span style={{ fontSize: '12px', display: 'block', whiteSpace: 'inherit', overflowWrap: 'anywhere' }}>{v || '—'}</span>,
      exportValue: r => r.isyeri_adresi,
    },
    { key: 'mahalle', label: 'Mahalle', hidden: true, render: v => v || '—', exportValue: r => r.mahalle },
    { key: 'sokak', label: 'Cadde/Sokak', hidden: true, render: v => v || '—', exportValue: r => r.sokak },
    { key: 'is_diskapi', label: 'Dış Kapı No', hidden: true, render: v => v || '—', exportValue: r => r.is_diskapi },
    { key: 'is_ickapi', label: 'İç Kapı No', hidden: true, render: v => v || '—', exportValue: r => r.is_ickapi },
    { key: 'is_katno', label: 'Kat No', hidden: true, render: v => v || '—', exportValue: r => r.is_katno },
    { key: 'is_il', label: 'İl', hidden: true, render: v => v || '—', exportValue: r => r.is_il },
    { key: 'is_ilce', label: 'İlçe', hidden: true, render: v => v || '—', exportValue: r => r.is_ilce },
    { key: 'ada', label: 'Ada', hidden: true, render: v => v || '—', exportValue: r => r.ada },
    { key: 'pafta', label: 'Pafta', hidden: true, render: v => v || '—', exportValue: r => r.pafta },
    { key: 'parsel', label: 'Parsel', hidden: true, render: v => v || '—', exportValue: r => r.parsel },
    {
      key: 'ana_faaliyet_adi', label: 'Ana Faaliyet',
      render: v => <span style={{ fontSize: '12px' }}>{v || '—'}</span>,
      exportValue: r => r.ana_faaliyet_adi,
    },
    {
      key: 'tali_faaliyetler', label: 'Tali Faaliyet', hidden: true,
      render: v => <span style={{ fontSize: '12px' }}>{v || '—'}</span>,
      exportValue: r => r.tali_faaliyetler,
    },
    {
      key: 'sinif_adi', label: 'Sınıf', width: '100px',
      render: (v, r) => {
        const s = v || r.faaliyet_sinifi;
        return s ? <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '10px', background: '#e8eaf6', color: '#3949ab' }}>{s}</span> : '—';
      },
      exportValue: r => r.sinif_adi || r.faaliyet_sinifi,
    },
    { key: 'kapali_alan', label: 'Kapalı Alan (m²)', hidden: true, render: v => v || '—', exportValue: r => r.kapali_alan },
    { key: 'acik_alan', label: 'Açık Alan (m²)', hidden: true, render: v => v || '—', exportValue: r => r.acik_alan },
    { key: 'kapasite', label: 'Kapasite', hidden: true, render: v => v || '—', exportValue: r => r.kapasite },
    { key: 'kapasite_tip', label: 'Kapasite Tipi', hidden: true, render: v => v || '—', exportValue: r => r.kapasite_tip },
    { key: 'oda_sayisi', label: 'Oda Sayısı', hidden: true, render: v => v || '—', exportValue: r => r.oda_sayisi },
    { key: 'sgk_sayisi', label: 'SGK Çalışan', hidden: true, render: v => v || '—', exportValue: r => r.sgk_sayisi },
    { key: 'mulk', label: 'Mülkiyet', hidden: true, render: v => v || '—', exportValue: r => r.mulk },
    { key: 'yer', label: 'Yer', hidden: true, render: v => v || '—', exportValue: r => r.yer },
    { key: 'isyeri_tel', label: 'İşyeri Telefonu', hidden: true, render: v => v || '—', exportValue: r => r.isyeri_tel },
    { key: 'isyeri_eposta', label: 'İşyeri E-posta', hidden: true, render: v => v || '—', exportValue: r => r.isyeri_eposta },
    { key: 'yaz_acilis_saati', label: 'Yaz Açılış', hidden: true, render: v => v || '—', exportValue: r => r.yaz_acilis_saati },
    { key: 'yaz_kapanis_saati', label: 'Yaz Kapanış', hidden: true, render: v => v || '—', exportValue: r => r.yaz_kapanis_saati },
    { key: 'kis_acilis_saati', label: 'Kış Açılış', hidden: true, render: v => v || '—', exportValue: r => r.kis_acilis_saati },
    { key: 'kis_kapanis_saati', label: 'Kış Kapanış', hidden: true, render: v => v || '—', exportValue: r => r.kis_kapanis_saati },
    { key: 'calisma_gunleri', label: 'Çalışma Günleri', hidden: true, render: v => v || '—', exportValue: r => r.calisma_gunleri },
    { key: 'alkol_ruhsati', label: 'Alkol Ruhsatı', hidden: true, render: v => v ? 'Var' : '—', exportValue: r => r.alkol_ruhsati ? 'Var' : '' },
    { key: 'alkol_turu', label: 'Alkol Türü', hidden: true, render: v => v || '—', exportValue: r => r.alkol_turu },
    { key: 'nace_kodu', label: 'NACE Kodu', hidden: true, render: v => v || '—', exportValue: r => r.nace_kodu },
    { key: 'gayrisihhi_sinif', label: 'GSM Sınıf', hidden: true, render: v => v || '—', exportValue: r => r.gayrisihhi_sinif },
    { key: 'yangin_rapor_no', label: 'Yangın Rapor No', hidden: true, render: v => v || '—', exportValue: r => r.yangin_rapor_no },
    { key: 'yangin_rapor_tarihi', label: 'Yangın Rapor Tarihi', hidden: true, render: v => fmt(v), exportValue: r => fmt(r.yangin_rapor_tarihi) },
    { key: 'imar_durumu', label: 'İmar Durumu', hidden: true, render: v => v || '—', exportValue: r => r.imar_durumu },
    { key: 'yapi_kullanim_izni', label: 'Yapı Kull. İzni', hidden: true, render: v => v || '—', exportValue: r => r.yapi_kullanim_izni },
    { key: 'takip_suresi', label: 'Takip Süresi', hidden: true, render: v => v || '—', exportValue: r => r.takip_suresi },
    { key: 'trzm_belgeno', label: 'Turizm Belge No', hidden: true, render: v => v || '—', exportValue: r => r.trzm_belgeno },
    { key: 'ebelgeno', label: 'E-Belge No', width: '150px', render: v => v || '—', exportValue: r => r.ebelgeno },
    { key: 'ebelgetarih', label: 'E-Belge Tarihi', hidden: true, render: v => fmt(v), exportValue: r => fmt(r.ebelgetarih) },
    { key: 'depo_adresi', label: 'Depo Adresi', hidden: true, render: v => v || '—', exportValue: r => r.depo_adresi },
    {
      key: 'durum', label: 'Durum', width: '100px',
      render: v => <span className={DURUMLAR[v] || 'badge'}>{DURUM_ETIKET[v] || v}</span>,
      exportValue: r => DURUM_ETIKET[r.durum] || r.durum,
    },
    {
      key: 'basvuru_tarihi', label: 'Başvuru Tarihi', width: '110px',
      render: v => <span style={{ fontSize: '12px' }}>{fmt(v)}</span>,
      exportValue: r => fmt(r.basvuru_tarihi),
    },
    { key: 'onay_tarihi', label: 'Onay Tarihi', width: '110px', hidden: true, render: v => fmt(v), exportValue: r => fmt(r.onay_tarihi) },
    { key: 'ruhsat_bitis_tarihi', label: 'Bitiş Tarihi', width: '110px', hidden: true, render: v => fmt(v), exportValue: r => fmt(r.ruhsat_bitis_tarihi) },
    { key: 'gecici_ruhsat', label: 'Geçici Ruhsat', hidden: true, render: v => v ? 'Evet' : '—', exportValue: r => r.gecici_ruhsat ? 'Evet' : '' },
    { key: 'kayit_zamani', label: 'Kayıt Zamanı', hidden: true, render: v => fmt(v), exportValue: r => fmt(r.kayit_zamani) },
    {
      key: '_islem', label: 'İşlem', width: '120px', sortable: false, filterable: false,
      render: (_, r) => (
        <div style={{ display: 'flex', gap: '4px' }}>
          <button className="btn btn-outline btn-xs" onClick={e => { e.stopPropagation(); navigate(`/ruhsat/${r.id}`); }}>Detay</button>
          <button className="btn btn-secondary btn-xs" onClick={e => { e.stopPropagation(); navigate(`/ruhsat/${r.id}/duzenle`); }}>Düzenle</button>
        </div>
      ),
    },
  ];

  return (
    <div>
      {smsModal && (
        <div className="form-modal-backdrop" onMouseDown={() => { if (!smsGonderiyor) setSmsModal(false); }}>
          <div className="form-modal form-modal-wide" role="dialog" aria-modal="true" onMouseDown={e => e.stopPropagation()} style={{ maxWidth: '620px' }}>
            <h3>📱 SMS Gönder</h3>
            {smsSonuc ? (
              <div>
                {smsSonuc.hata ? (
                  <div className="alert alert-error">{smsSonuc.hata}</div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '16px 0' }}>
                    <div style={{ fontSize: '32px', marginBottom: '8px' }}>✅</div>
                    <div style={{ fontSize: '15px', fontWeight: '600', marginBottom: '12px' }}>SMS Gönderimi Tamamlandı</div>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', fontSize: '14px' }}>
                      <div style={{ color: '#2e7d32' }}><strong>{smsSonuc.gonderilen}</strong> Gönderildi</div>
                      <div style={{ color: '#e65100' }}><strong>{smsSonuc.atlandi}</strong> Atlandı (GSM yok)</div>
                      <div style={{ color: '#c62828' }}><strong>{smsSonuc.basarisiz}</strong> Başarısız</div>
                    </div>
                  </div>
                )}
                <div className="form-modal-actions">
                  <button className="btn btn-secondary" onClick={() => setSmsModal(false)}>Kapat</button>
                </div>
              </div>
            ) : (
              <>
                {/* Hedef seçimi */}
                <div style={{ marginBottom: '14px', padding: '10px 14px', background: '#f5f7ff', borderRadius: '6px', fontSize: '13px', display: 'flex', gap: '20px', alignItems: 'center' }}>
                  <strong style={{ color: '#555' }}>Hedef:</strong>
                  <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <input type="radio" checked={smsHedefTum} onChange={() => setSmsHedefTum(true)} />
                    <span>Filtredeki tüm liste <strong>({tumVeri.length} kayıt)</strong></span>
                  </label>
                  <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <input type="radio" checked={!smsHedefTum} onChange={() => setSmsHedefTum(false)} />
                    <span>Tabloda seçilenler <strong>({smsSecimler.length} seçili)</strong></span>
                  </label>
                </div>

                {/* Şablon kartları */}
                {smsSablonlar.length > 0 && (
                  <div style={{ marginBottom: '14px' }}>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '8px' }}>SMS Şablonu Seç:</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {smsSablonlar.map(s => {
                        const secili = String(smsSablonId) === String(s.id);
                        return (
                          <button
                            key={s.id}
                            onClick={() => { setSmsSablonId(s.id); setSmsMesaj(s.icerik || ''); }}
                            style={{
                              padding: '6px 14px', border: `2px solid ${secili ? '#1a237e' : '#ddd'}`,
                              borderRadius: '20px', cursor: 'pointer', fontSize: '12px', fontWeight: secili ? '700' : '400',
                              background: secili ? '#e8eaf6' : '#fff', color: secili ? '#1a237e' : '#444',
                              transition: 'all 0.15s',
                            }}
                          >
                            {secili ? '✓ ' : ''}{s.sablon_adi}
                          </button>
                        );
                      })}
                      {smsSablonId && (
                        <button
                          onClick={() => { setSmsSablonId(''); setSmsMesaj(''); }}
                          style={{ padding: '6px 12px', border: '1px dashed #ccc', borderRadius: '20px', cursor: 'pointer', fontSize: '12px', background: 'none', color: '#999' }}
                        >✕ Şablonu temizle</button>
                      )}
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label>
                    Mesaj İçeriği *
                    <span style={{ color: '#999', fontWeight: '400', marginLeft: '8px' }}>
                      {(smsMesaj||'').length} karakter · Değişkenler: {'{{unvan}}'} {'{{ruhsat_no}}'} {'{{durum}}'} {'{{ad}}'}
                    </span>
                  </label>
                  <textarea
                    value={smsMesaj}
                    onChange={e => { setSmsMesaj(e.target.value); setSmsSablonId(''); }}
                    rows={4}
                    placeholder="Sayın {{ad}}, {{unvan}} işyerinize ait ruhsat başvurusu güncellenmiştir."
                    style={{ fontFamily: 'monospace', fontSize: '13px' }}
                  />
                </div>
                <div className="form-modal-actions">
                  <button className="btn btn-secondary" onClick={() => setSmsModal(false)}>İptal</button>
                  <button
                    className="btn btn-primary"
                    onClick={smsGonder}
                    disabled={smsGonderiyor || !(smsHedefTum ? tumVeri.length : smsSecimler.length) || !smsMesaj}
                  >
                    {smsGonderiyor ? '⏳ Gönderiliyor...' : `📱 ${smsHedefTum ? tumVeri.length : smsSecimler.length} Kişiye SMS Gönder`}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ color: '#1a237e', fontSize: '20px' }}>
          📋 Ruhsat Listesi
          {!tumRuhsatlar && <span style={{ fontSize: '13px', fontWeight: '400', color: '#888', marginLeft: '10px' }}>({bugunTarih} tarihli)</span>}
        </h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          {!tumRuhsatlar ? (
            <button className="btn btn-outline btn-sm" onClick={() => { setTumRuhsatlar(true); setFiltre(f => ({ ...f, basvuru_tarihi: '' })); setSayfa(1); }}>
              📂 Tüm Ruhsatlar
            </button>
          ) : (
            <button className="btn btn-outline btn-sm" onClick={() => { setTumRuhsatlar(false); setFiltre(f => ({ ...f, basvuru_tarihi: bugunTarih })); setSayfa(1); }}>
              📅 Bugün
            </button>
          )}
          <button className="btn btn-outline" onClick={smsPanelAc} style={{ position: 'relative' }}>
            📱 SMS Gönder
            {smsSecimler.length > 0 && (
              <span style={{ position: 'absolute', top: '-6px', right: '-6px', background: '#e53935', color: '#fff', borderRadius: '10px', padding: '1px 6px', fontSize: '10px', fontWeight: '700' }}>
                {smsSecimler.length}
              </span>
            )}
          </button>
          <Link to="/ruhsat/yeni" className="btn btn-primary">➕ Yeni Başvuru</Link>
        </div>
      </div>

      <div className="card">
        <DataTable
          columns={columns}
          data={tumVeri.length > 0 ? tumVeri : ruhsatlar}
          filename="ruhsat-listesi"
          pdfTitle="Ruhsat Listesi — Ünye Belediyesi"
          loading={yukleniyor}
          rowKey="id"
          onRowClick={row => navigate(`/ruhsat/${row.id}`)}
          selectable={true}
          selectedRows={smsSecimler}
          onSelectionChange={setSmsSecimler}
          getRowStyle={row => row.durum === 'IPTAL' ? { color: '#c62828', fontWeight: '600' } : undefined}
          defaultWrapText={true}
          toolbar={
            <div className="sicil-toolbar-controls" style={{ flex: 1 }}>
              <input
                className="search-input sicil-search-input"
                placeholder="🔍 Ünvan, TC, Vergi No, Ruhsat No, Adres..."
                value={filtre.q}
                onChange={e => { setFiltre({ ...filtre, q: e.target.value }); setSayfa(1); }}
              />
              <select value={filtre.durum} onChange={e => { setFiltre({ ...filtre, durum: e.target.value }); setSayfa(1); }}>
                <option value="">Tüm Durumlar</option>
                {Object.entries(DURUM_ETIKET).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <select value={filtre.sinif_adi} onChange={e => { setFiltre({ ...filtre, sinif_adi: e.target.value }); setSayfa(1); }}>
                <option value="">Tüm Sınıflar</option>
                <option value="Sıhhi">Sıhhi</option>
                <option value="Gayrisıhhi">Gayrisıhhi</option>
                <option value="Umuma Açık">Umuma Açık</option>
              </select>
              <input
                type="number" placeholder="Yıl" value={filtre.yil}
                onChange={e => { setFiltre({ ...filtre, yil: e.target.value }); setSayfa(1); }}
                style={{ width: '80px' }}
              />
              <button onClick={yukle} className="btn btn-secondary btn-sm">🔄 Yenile</button>
              <span style={{ fontSize: '12px', color: '#888', whiteSpace: 'nowrap' }}>
                <strong>{toplam}</strong> kayıt
                {tumVeri.length > ruhsatlar.length && <span style={{ color: '#3949ab' }}> (export: {tumVeri.length})</span>}
              </span>
            </div>
          }
        />

      </div>
    </div>
  );
}
